import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/axios";
import FormPreview from "../components/FormPreview";
import "../assets/styles/page.css";
import { FiTrash2 } from "react-icons/fi";
import { Tooltip } from "react-tooltip";

const PAGE_NAME = "Forms";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const inputTypes = [
  "text",
  "number",
  "email",
  "tel",
  "checkbox",
  "radio",
  "select",
  "textarea",
  "date",
  "file",
];

const emptyForm = {
  sub_category_id: "",
  title: "",
  description: "",
  status: "active",
  fields: [],
};

const emptyField = () => ({
  field_label: "",
  field_name: "",
  input_type: "text",
  optionsText: "",
  placeholder: "",
  help_text: "",
  min_length: "",
  max_length: "",
  min_value: "",
  max_value: "",
  max_file_size_mb: "",
  allowedFileTypesText: "",
  is_required: true,
  status: "active",
});

const slugifyFieldName = (value = "") =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const splitComma = (value = "") =>
  String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const FormBuilder = () => {
  const navigate = useNavigate();
  const { form_id } = useParams();
  const isEdit = Boolean(form_id);

  const [subCategories, setSubCategories] = useState([]);
  const [formData, setFormData] = useState({
    ...emptyForm,
    fields: [emptyField()],
  });

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const previewForm = useMemo(() => {
    return {
      title: formData.title || "Untitled Form",
      description: formData.description,
      status: formData.status,
      sub_category:
        subCategories.find(
          (item) =>
            String(item.sub_category_id) === String(formData.sub_category_id)
        ) || null,
      fields: formData.fields
        .filter((field) => field.field_label.trim())
        .map((field, index) => ({
          form_field_id: index,
          field_label: field.field_label,
          field_name: field.field_name || slugifyFieldName(field.field_label),
          input_type: field.input_type,
          options: ["select", "radio", "checkbox"].includes(field.input_type)
            ? splitComma(field.optionsText)
            : null,
          placeholder: field.placeholder,
          help_text: field.help_text,
          min_length: field.min_length,
          max_length: field.max_length,
          min_value: field.min_value,
          max_value: field.max_value,
          max_file_size_mb: field.max_file_size_mb,
          allowed_file_types:
            field.input_type === "file" && field.allowedFileTypesText
              ? splitComma(field.allowedFileTypesText)
              : null,
          is_required: field.is_required,
          sort_order: index,
          status: field.status,
        })),
    };
  }, [formData, subCategories]);

  const fetchMeta = async () => {
    const res = await api.get("/admin/products/sub-categories", {
      headers: getPermissionHeaders("view"),
    });
    setSubCategories(res.data?.data || []);
  };

  const fetchForm = async () => {
    if (!isEdit) return;

    const res = await api.get(`/admin/forms/${form_id}`, {
      headers: getPermissionHeaders("view"),
    });
    const form = res.data?.data;

    setFormData({
      sub_category_id: form.sub_category_id || "",
      title: form.title || "",
      description: form.description || "",
      status: form.status || "active",
      fields:
        form.fields?.length > 0
          ? form.fields.map((field) => ({
            field_label: field.field_label || "",
            field_name: field.field_name || "",
            input_type: field.input_type || "text",
            optionsText: Array.isArray(field.options)
              ? field.options.join(", ")
              : "",
            placeholder: field.placeholder || "",
            help_text: field.help_text || "",
            min_length: field.min_length || "",
            max_length: field.max_length || "",
            min_value: field.min_value || "",
            max_value: field.max_value || "",
            max_file_size_mb: field.max_file_size_mb || "",
            allowedFileTypesText: Array.isArray(field.allowed_file_types)
              ? field.allowed_file_types.join(", ")
              : "",
            is_required: Boolean(field.is_required),
            status: field.status || "active",
          }))
          : [emptyField()],
    });
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await fetchMeta();
        await fetchForm();
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to load form builder."
        );
        navigate("/forms");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [form_id]);

  const addField = () => {
    setFormData((prev) => ({
      ...prev,
      fields: [...prev.fields, emptyField()],
    }));
  };

  const removeField = (index) => {
    setFormData((prev) => ({
      ...prev,
      fields:
        prev.fields.length > 1
          ? prev.fields.filter((_, fieldIndex) => fieldIndex !== index)
          : [emptyField()],
    }));
  };

  const updateField = (index, key, value) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((field, fieldIndex) => {
        if (fieldIndex !== index) return field;

        const updated = {
          ...field,
          [key]: value,
        };

        if (key === "field_label" && !field.field_name) {
          updated.field_name = slugifyFieldName(value);
        }

        if (!["select", "radio", "checkbox"].includes(updated.input_type)) {
          updated.optionsText = "";
        }

        if (!["text", "email", "tel", "textarea"].includes(updated.input_type)) {
          updated.min_length = "";
          updated.max_length = "";
        }

        if (updated.input_type !== "number") {
          updated.min_value = "";
          updated.max_value = "";
        }

        if (updated.input_type !== "file") {
          updated.max_file_size_mb = "";
          updated.allowedFileTypesText = "";
        }

        return updated;
      }),
    }));
  };

  const buildFieldsPayload = () => {
    return formData.fields
      .filter((field) => field.field_label.trim())
      .map((field, index) => ({
        field_label: field.field_label,
        field_name: field.field_name || slugifyFieldName(field.field_label),
        input_type: field.input_type,

        options: ["select", "radio", "checkbox"].includes(field.input_type)
          ? splitComma(field.optionsText)
          : null,

        placeholder: field.placeholder || null,
        help_text: field.help_text || null,

        min_length: field.min_length || null,
        max_length: field.max_length || null,
        min_value: field.min_value || null,
        max_value: field.max_value || null,
        max_file_size_mb: field.max_file_size_mb || null,

        allowed_file_types:
          field.input_type === "file" && field.allowedFileTypesText
            ? splitComma(field.allowedFileTypesText)
            : null,

        is_required: field.is_required,
        sort_order: index,
        status: field.status || "active",
      }));
  };

  const submitForm = async (event) => {
    event.preventDefault();

    if (!formData.sub_category_id || !formData.title.trim()) {
      toast.error("Sub-category and form title are required.");
      return;
    }

    const fieldsPayload = buildFieldsPayload();

    if (!fieldsPayload.length) {
      toast.error("Add at least one form field.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        sub_category_id: formData.sub_category_id,
        title: formData.title,
        description: formData.description,
        status: formData.status,
        fields: fieldsPayload,
      };

      if (isEdit) {
        await api.put(`/admin/forms/${form_id}`, payload, {
          headers: getPermissionHeaders("edit"),
        });
        toast.success("Form updated successfully.");
      } else {
        await api.post("/admin/forms", payload, {
          headers: getPermissionHeaders("add"),
        });
        toast.success("Form created successfully.");
      }

      navigate("/forms");
    } catch (error) {
      showRequestError(error, "Form save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="admin-page">
        <div className="page-card form-loader-card">
          <div className="premium-loader"></div>
          <h2>Loading form builder</h2>
          <p>Please wait while the form data is prepared.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-page form-builder-page">
      <div className="page-header forms-hero-header">
        <div>
          <p>Form Builder</p>
          <h1>{isEdit ? "Edit Form" : "Add Form"}</h1>
        </div>

        <button
          type="button"
          className="secondary-action"
          onClick={() => navigate("/forms")}
        >
          Back to Forms
        </button>
      </div>

      <form className="form-builder-layout" onSubmit={submitForm}>
        <div className="form-builder-left">
          <div className="page-card builder-section-card">
            <h2>Form Details</h2>

            <div className="builder-grid">
              <label>
                Sub-category
                <select
                  value={formData.sub_category_id}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      sub_category_id: event.target.value,
                    }))
                  }
                >
                  <option value="">Select sub-category</option>
                  {subCategories.map((subCategory) => (
                    <option
                      key={subCategory.sub_category_id}
                      value={subCategory.sub_category_id}
                    >
                      {subCategory.category?.name
                        ? `${subCategory.category.name} / ${subCategory.name}`
                        : subCategory.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Status
                <select
                  value={formData.status}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: event.target.value,
                    }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>

              <label>
                Form Title
                <input
                  value={formData.title}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      title: event.target.value,
                    }))
                  }
                  placeholder="Invitation Customisation Form"
                />
              </label>
            </div>

            <label>
              Description
              <textarea
                value={formData.description}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
                rows="3"
              />
            </label>
          </div>

          <div className="page-card builder-section-card">
            <div className="field-builder-header">
              <div>
                <h2>Fields</h2>
                <p>Add text, select, file upload and custom fields.</p>
              </div>
            </div>

            <div className="field-builder-list">
              {formData.fields.map((field, index) => (
                <div className="field-builder-card" key={index}>
                  <div className="field-card-top">
                    <strong>Field {index + 1}</strong>

                    <button
                      type="button"
                      className="ghost-action"
                      data-tooltip-id={`deleteTip-${index}`}
                      data-tooltip-content="Delete"
                      onClick={() => removeField(index)}
                    >
                      <FiTrash2 />
                    </button>

                    <Tooltip id={`deleteTip-${index}`} />
                  </div>

                  <div className="field-grid">
                    <label>
                      Label
                      <input
                        value={field.field_label}
                        onChange={(event) =>
                          updateField(index, "field_label", event.target.value)
                        }
                        placeholder="Bride Name"
                      />
                    </label>

                    <label>
                      Field Name
                      <input
                        value={field.field_name}
                        onChange={(event) =>
                          updateField(index, "field_name", event.target.value)
                        }
                        placeholder="Auto generated if empty"
                      />
                    </label>

                    <label>
                      Input Type
                      <select
                        value={field.input_type}
                        onChange={(event) =>
                          updateField(index, "input_type", event.target.value)
                        }
                      >
                        {inputTypes.map((type) => {
                          const labels = {
                            text: "Text",
                            number: "Number",
                            email: "Email",
                            tel: "Telephone",
                            checkbox: "Checkbox",
                            radio: "Radio",
                            select: "Dropdown",
                            textarea: "Textarea",
                            date: "Date",
                            file: "File Upload",
                          };

                          return (
                            <option key={type} value={type}>
                              {labels[type] || type}
                            </option>
                          );
                        })}
                      </select>
                    </label>

                    <label>
                      Placeholder
                      <input
                        value={field.placeholder}
                        onChange={(event) =>
                          updateField(index, "placeholder", event.target.value)
                        }
                        placeholder="Enter value"
                      />
                    </label>

                    <label>
                      Help Text
                      <input
                        value={field.help_text}
                        onChange={(event) =>
                          updateField(index, "help_text", event.target.value)
                        }
                        placeholder="Small info shown below the field"
                      />
                    </label>

                    <label>
                      Status
                      <select
                        value={field.status}
                        onChange={(event) =>
                          updateField(index, "status", event.target.value)
                        }
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </label>

                    <label className="checkbox-label custom-checkbox">
                      <input
                        type="checkbox"
                        checked={field.is_required}
                        onChange={(event) =>
                          updateField(
                            index,
                            "is_required",
                            event.target.checked
                          )
                        }
                      />
                      <span className="checkmark"></span>
                      Required Field
                    </label>
                  </div>

                  {["select", "radio", "checkbox"].includes(
                    field.input_type
                  ) && (
                      <label>
                        Options
                        <input
                          value={field.optionsText}
                          onChange={(event) =>
                            updateField(index, "optionsText", event.target.value)
                          }
                          placeholder="English, Hindi, Bengali"
                        />
                      </label>
                    )}

                  {["text", "email", "tel", "textarea"].includes(
                    field.input_type
                  ) && (
                      <div className="field-grid mt-3">
                        <label>
                          Min Length
                          <input
                            type="number"
                            value={field.min_length}
                            onChange={(event) =>
                              updateField(
                                index,
                                "min_length",
                                event.target.value
                              )
                            }
                            placeholder="0"
                          />
                        </label>

                        <label>
                          Max Length
                          <input
                            type="number"
                            value={field.max_length}
                            onChange={(event) =>
                              updateField(
                                index,
                                "max_length",
                                event.target.value
                              )
                            }
                            placeholder="100"
                          />
                        </label>
                      </div>
                    )}

                  {field.input_type === "number" && (
                    <div className="field-grid mt-3">
                      <label>
                        Min Number
                        <input
                          type="number"
                          value={field.min_value}
                          onChange={(event) =>
                            updateField(
                              index,
                              "min_value",
                              event.target.value
                            )
                          }
                          placeholder="0"
                        />
                      </label>

                      <label>
                        Max Number
                        <input
                          type="number"
                          value={field.max_value}
                          onChange={(event) =>
                            updateField(
                              index,
                              "max_value",
                              event.target.value
                            )
                          }
                          placeholder="999"
                        />
                      </label>
                    </div>
                  )}

                  {field.input_type === "file" && (
                    <div className="field-grid mt-3">
                      <label>
                        Max File Size MB
                        <input
                          type="number"
                          value={field.max_file_size_mb}
                          onChange={(event) =>
                            updateField(
                              index,
                              "max_file_size_mb",
                              event.target.value
                            )
                          }
                          placeholder="5"
                        />
                      </label>

                      <label>
                        Allowed File Types
                        <input
                          value={field.allowedFileTypesText}
                          onChange={(event) =>
                            updateField(
                              index,
                              "allowedFileTypesText",
                              event.target.value
                            )
                          }
                          placeholder="jpg, png, pdf"
                        />
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div>
              <button type="button" className="primary-action" onClick={addField}>
                + Add Field
              </button>
            </div>
          </div>
        </div>

        <div className="form-builder-right">
          <FormPreview form={previewForm} />

          <div className="builder-save-card">
            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={() => navigate("/forms")}
            >
              Cancel
            </button>

            <button type="submit" className="primary-action" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Update Form" : "Create Form"}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
};

export default FormBuilder;